import type { TargetState, Transition } from "@uirouter/react";
import { find, isEmpty, isUndefined } from "lodash";

import { onUnsupportedWebSourceRedirect } from "app/actions/commonActions";
import { swSettings } from "app/common/services/swSettings";
import { getAvailableWebSource } from "app/components/filters-bar/utils";
import { getDomainWithSubs } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import { store } from "single-spa/store/store";

export const FORCE_PRO_PARAM = "forcePro";

export function webSourceNavChangeHandler(transition: Transition): TargetState | undefined {
    /*
WebSource Logic:
*/
    const toState = transition.to() as IRouterState;
    const toParams = { ...transition.params() };
    const fromParams = transition.params("from");
    const webSourceFromUrl = toParams.webSource;
    let webSource;
    const pageWebSourcesFn = getAvailableWebSource(toState, toParams);
    const selectedWebSource: any = find(pageWebSourcesFn, {
        id: webSourceFromUrl,
    });
    const firstEnabledWebSource: any = find(pageWebSourcesFn, {
        disabled: false,
    });

    if (isUndefined(firstEnabledWebSource)) {
        // no webSource on page
        return;
    }
    if (isUndefined(selectedWebSource) || selectedWebSource.disabled) {
        webSource = firstEnabledWebSource.id;
        // fire event only if there is a webSource in URL
        // if there isn't, we don't need to show a tooltip to the user.
        const reload = isEmpty(fromParams) ? true : fromParams.webSource !== toParams.webSource;
        if (webSourceFromUrl) {
            setTimeout(() => {
                store.dispatch(onUnsupportedWebSourceRedirect());
            }, 100);
        }
        const goToParams = Object.assign({}, toParams, { webSource });
        return transition.router.stateService.target(toState, goToParams, {
            reload,
            location: "replace",
        });
    }
}

export function isCustomDurationAllowed(hideCalendar: any) {
    return !hideCalendar;
}

// Some component configs return a `defaultParams.duration` that is itself locked/disabled
// (e.g. trial users where `defaultParams.duration = "6m"` but the `6m` preset is locked).
// When the router redirects to fix an unsupported duration, naively using `defaultParams.duration`
// can produce a redirect to the same unsupported duration and stall the transition (DMI-17958).
// Fall back to the first preset that `allowedDuration` actually accepts for the target state.
export const resolveFallbackAllowedDuration = (
    componentId: string | null | undefined,
    routerState: IRouterState | undefined,
): string | undefined => {
    const component = componentId ? swSettings.components[componentId] : swSettings.current;
    const defaultDuration: string | undefined = component?.defaultParams?.duration;

    if (defaultDuration && swSettings.allowedDuration(defaultDuration, componentId ?? undefined, routerState)) {
        return defaultDuration;
    }

    const allowedPreset = (component?.datePickerPresets as Array<{ value: string }> | undefined)?.find((preset) =>
        swSettings.allowedDuration(preset.value, componentId ?? undefined, routerState),
    );

    return allowedPreset?.value ?? defaultDuration;
};

export const checkIfNonDynamicParamsChanged = (
    toState: FixmeType,
    fromParams: FixmeType,
    toParams: FixmeType,
): boolean => {
    const params: Record<string, any> = toState?.params;
    if (!params) {
        return false;
    }
    if (params?.dynamic === false) {
        return true;
    }

    const paramsEntries = Object.entries(params);
    for (let i = 0; i < paramsEntries.length; i++) {
        const name = paramsEntries[i][0];
        const value = paramsEntries[i][1];
        if (value?.dynamic === false && fromParams[name] !== toParams[name]) {
            return true;
        }
    }
    return false;
};

export const validateWebsiteKey = (
    toParams: FixmeType,
    toState: FixmeType,
    transition: Transition,
): TargetState | undefined => {
    const { key } = toParams;
    if (key) {
        const domains: string[] = key.split(",");
        if (domains.length > 1) {
            const newKey: string[] = [];
            let shouldReroute = false;
            domains.forEach((domain: string) => {
                const domainWithSubs = getDomainWithSubs(domain);
                if (domainWithSubs !== domain) {
                    shouldReroute = true;
                }
                if (domainWithSubs) {
                    newKey.push(domainWithSubs);
                }
            });
            if (shouldReroute) {
                return transition.router.stateService.target(toState, {
                    ...toParams,
                    key: newKey.toString(),
                });
            }
        } else {
            const domainWithSubs = getDomainWithSubs(key);
            if (domainWithSubs !== key) {
                return transition.router.stateService.target(toState, {
                    ...toParams,
                    key: domainWithSubs,
                });
            }
        }
    }
};
